// `MulticamScript`, `MulticamLayout`, `MulticamTypst`, `MulticamDocx` (SuiteCompile/Multicam.swift; `design-docs/Musicals,
// radio and sitcoms — design.md`, step 3): a multi-camera sitcom on the screenplay's page and columns — every act and
// scene from the top of a page under its act heading and scene letter, the slugline underlined with the scene's cast
// under it, action in capitals with entrances, exits and first appearances underlined, dialogue double-spaced, END OF
// ACT … after a numbered act and END OF SHOW at the end.
import { fountainParagraphs, fountainParagraphText, fountainSpeakers, fountainSpeakersOfCue } from '@writing-suite/core';
import { DOCX_SETTINGS, docxApp, docxCore, docxCustom, twips, whole } from '../docx.ts';
import { pageInches } from '../manuscript.ts';
import type { CompiledManuscript, RenderSettings } from '../types.ts';
import { xmlEscape } from '../xml.ts';
import { ZipWriter } from '../zip.ts';
import {
  addContinueds, count, LEFT_MARGIN, length, run, screenplayScript, speaker, TOP_MARGIN, WIDTH, wrap,
  type Element, type Paragraph, type Run, type ScriptPage, type TitleField,
} from './script.ts';
import {
  columnsOf, DUAL_LEFT, DUAL_RIGHT, linesForPage, Paginator, piece, speechHeight,
  type Piece, type Segment, type Speech, type SpeechColumns, type Unit,
} from './stage.ts';
import { docxBits } from './stageDocx.ts';
import { markup, screenplayFontFamilies, screenplayPreamble, screenplayTitlePage } from './typst.ts';

const COLUMN = 7.2;
const LINE = 12.0;
const num = (d: number) => d.toFixed(2);
const blank = (s: string) => /^\p{White_Space}*$/u.test(s);
const cloneParagraph = (p: Paragraph): Paragraph => ({ ...p, lines: p.lines.map((l) => l.map((r) => ({ ...r }))) });

export interface MulticamScene {
  /** "A", "B"… through the episode. */
  letter: string;
  /** The act's heading over its first scene ("COLD OPEN"); null for its other scenes, or with no acts. */
  actHeading: string | null;
  /** The scene's slugline as written (its first scene heading). */
  slugline: Run[] | null;
  /** Who speaks in it, in capitals, in order of first speaking. */
  cast: string[];
  paragraphs: Paragraph[];
  /** "END OF ACT ONE" after a numbered act's last scene, "END OF SHOW" after the episode's. */
  end: string | null;
  fragmentID: string | null;
}

export interface MulticamScript { title: string; titlePage: TitleField[]; scenes: MulticamScene[] }

/** `MulticamScript.letter`: A–Z, then AA, BB… */
export const sceneLetter = (index: number): string => 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'[index % 26].repeat(Math.floor(index / 26) + 1);

/** `MulticamScript.isNumberedAct`: "Act One", "Act 2" take an END OF ACT; a cold open or a tag doesn't. */
export const isNumberedAct = (title: string): boolean => /^\s*act\s+\S+/i.test(title);

const capitals = (runs: Run[]): Run[] => runs.map((r) => ({ ...r, text: r.text.toUpperCase() }));

/** `MulticamScript.underlined`: ENTERS, EXITS, ENTER, EXIT underlined, and a speaker's name the first time it appears in
 *  the episode's action (the spaces between marked words too). */
function underlined(runs: Run[], names: readonly string[], appeared: Set<string>): Run[] {
  const text = runs.map((r) => r.text).join('');
  const marks = new Array<boolean>(text.length).fill(false);
  const mark = (start: number, end: number) => { for (let i = start; i < end; i++) marks[i] = true; };
  const escapeRe = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  for (const word of ['ENTERS', 'EXITS', 'ENTER', 'EXIT']) {
    for (const m of text.matchAll(new RegExp(`\\b${word}\\b`, 'g'))) mark(m.index!, m.index! + m[0].length);
  }
  for (const name of [...names].sort((a, b) => count(b) - count(a))) {
    if (appeared.has(name)) continue;
    const m = new RegExp(`\\b${escapeRe(name)}\\b`).exec(text);
    if (m) { mark(m.index, m.index + m[0].length); appeared.add(name); }
  }
  if (!marks.includes(true)) return runs;
  for (let i = 0; i < text.length; i++) {
    if (text[i] !== ' ' || marks[i]) continue;
    let j = i;
    while (j < text.length && text[j] === ' ') j++;
    if (i > 0 && marks[i - 1] && j < text.length && marks[j]) mark(i, j);
  }
  const out: Run[] = [];
  let offset = 0;
  for (const r of runs) {
    for (const c of r.text) {
      const u = r.underline || marks[offset];
      const last = out[out.length - 1];
      if (last && last.italic === r.italic && last.bold === r.bold && last.underline === u) last.text += c;
      else out.push({ text: c, italic: r.italic, bold: r.bold, underline: u });
      offset += c.length;
    }
  }
  return out;
}

/** `MulticamScript.init`. */
export function multicamScript(m: CompiledManuscript): MulticamScript {
  const settings = m.preset.render.screenplay;
  interface Written { source: string; fragmentID: string | null }
  interface Act { title: string | null; scenes: Written[] }
  let acts: Act[] = [];
  for (const section of m.sections) {
    if (section.matter !== null) continue;
    const written = section.source !== null && !blank(section.source) ? section.source : null;
    if (section.role === 'part') {
      acts.push({ title: section.title, scenes: [] });
      if (written !== null) acts[acts.length - 1].scenes.push({ source: written, fragmentID: section.fragmentID });
    } else if (section.role === 'chapter' || section.role === 'scene') {
      if (!acts.length) acts.push({ title: null, scenes: [] });
      if (written !== null) acts[acts.length - 1].scenes.push({ source: written, fragmentID: section.fragmentID });
    }
  }
  acts = acts.filter((a) => a.scenes.length > 0);
  const appeared = new Set<string>();
  const speakers = [...new Set(acts.flatMap((a) => a.scenes).flatMap((s) => [...fountainSpeakers(s.source)]))];
  const scenes: MulticamScene[] = [];
  acts.forEach((act, a) => {
    act.scenes.forEach((written, s) => {
      const paragraphs = fountainParagraphs(written.source).map(cloneParagraph);
      if (settings?.continueds ?? true) addContinueds(paragraphs);
      let slugline: Run[] | null = null;
      if (paragraphs.length && paragraphs[0].element === 'sceneHeading') {
        slugline = paragraphs[0].lines[0] ?? null;
        paragraphs.splice(0, 1);
      }
      const cast: string[] = [];
      for (const p of paragraphs) {
        if (p.element !== 'character') continue;
        for (const name of fountainSpeakersOfCue(fountainParagraphText(p))) if (!cast.includes(name)) cast.push(name);
      }
      for (const p of paragraphs) if (p.element === 'action') p.lines = p.lines.map((l) => underlined(capitals(l), speakers, appeared));
      let end: string | null = null;
      if (s === act.scenes.length - 1) {
        if (a === acts.length - 1) end = 'END OF SHOW';
        else if (act.title !== null && isNumberedAct(act.title)) end = `END OF ${act.title.toUpperCase()}`;
      }
      scenes.push({ letter: sceneLetter(scenes.length), actHeading: s === 0 && act.title !== null ? act.title.toUpperCase() : null,
        slugline, cast, paragraphs, end, fragmentID: written.fragmentID });
    });
  });
  return { title: m.title, titlePage: screenplayScript(m).titlePage, scenes };
}

// MARK: Pagination

const SINGLE = columnsOf({ cue: 22, cueWidth: 38, paren: 16, parenWidth: 25, dialogue: 10, dialogueWidth: 35 });
const COLUMNS: SpeechColumns = { ...SINGLE, doubleSpaced: true };

/** `ScriptLayout.units`, with the play's paginator's types: the screenplay's elements as pagination moves them. */
function units(paragraphs: readonly Paragraph[]): Unit[] {
  const out: Unit[] = [];
  const speechElements = new Set<Element>(['dialogue', 'parenthetical', 'lyric']);
  const segmentsFrom = (start: number): [Segment[], number] => {
    const segments: Segment[] = [];
    let j = start;
    while (j < paragraphs.length && speechElements.has(paragraphs[j].element)) {
      const e = paragraphs[j].element;
      const kind = e === 'parenthetical' ? 'parenthetical' as const : e === 'lyric' ? 'lyric' as const : 'dialogue' as const;
      for (const line of paragraphs[j].lines) segments.push({ runs: line, kind });
      j += 1;
    }
    return [segments, j];
  };
  const speechAt = (start: number, c: SpeechColumns): [Speech, number] => {
    const cue = paragraphs[start];
    const [segments, next] = segmentsFrom(start + 1);
    const cueRuns = (cue.lines[0] ?? []).map((r) => ({ ...r, text: r.text.replaceAll('^', '') }));
    return [{ name: speaker(fountainParagraphText(cue)), cue: wrap(cueRuns, c.cueWidth).map((r) => piece(c.cue, r)), segments, columns: c }, next];
  };
  let i = 0;
  while (i < paragraphs.length) {
    const p = paragraphs[i];
    switch (p.element) {
      case 'pageBreak': out.push({ type: 'pageBreak' }); i += 1; break;
      case 'character': {
        const [, next] = segmentsFrom(i + 1);
        if (next < paragraphs.length && paragraphs[next].element === 'character' && paragraphs[next].isDual) {
          const [left, afterLeft] = speechAt(i, DUAL_LEFT);
          const [right, afterRight] = speechAt(afterLeft, DUAL_RIGHT);
          out.push({ type: 'dual', left, right });
          i = afterRight;
        } else {
          const [speech, after] = speechAt(i, SINGLE);
          out.push({ type: 'speech', speech });
          i = after;
        }
        break;
      }
      case 'dialogue': case 'parenthetical': {
        const [segments, after] = segmentsFrom(i);
        out.push({ type: 'speech', speech: { name: '', cue: [], segments, columns: SINGLE } });
        i = after;
        break;
      }
      case 'action':
        out.push({ type: 'action', segments: p.lines.map((runs) => ({ runs, kind: 'action' as const })) });
        i += 1;
        break;
      default: {
        const list: Piece[] = [];
        for (const line of p.lines) {
          if (p.element === 'transition') for (const w of wrap(line, WIDTH)) list.push(piece(WIDTH - length(w), w));
          else if (p.element === 'centered') for (const w of wrap(line, WIDTH)) list.push(piece(Math.trunc((WIDTH - length(w)) / 2), w));
          else if (p.element === 'lyric') list.push(...wrap(line.map((r) => ({ ...r, italic: true })), SINGLE.dialogueWidth).map((w) => piece(SINGLE.dialogue, w)));
          else for (const w of wrap(line, WIDTH)) list.push(piece(0, w));
        }
        out.push({ type: 'block', element: p.element, lines: list });
        i += 1;
      }
    }
  }
  return out;
}

export interface MulticamLayout {
  pages: ScriptPage[];
  /** Which scene each page belongs to. */
  pageScenes: number[];
  linesPerPage: number;
}

/** `MulticamLayout.init`. */
export function multicamLayout(script: MulticamScript, pageHeight = 11): MulticamLayout {
  const linesPerPage = linesForPage(pageHeight);
  const p = new Paginator(linesPerPage);
  const owners: number[] = [];
  const c = COLUMNS;
  const centred = (text: string) => piece(Math.max(0, Math.trunc((WIDTH - count(text)) / 2)), [run(text, { underline: true })]);
  const more = piece(c.cue, [run('(MORE)')]);
  const contd = (name: string) => [piece(c.cue, [run(`${name} (CONT'D)`)])];
  const doubled = (s: Speech): Speech => ({ ...s, columns: { ...s.columns, doubleSpaced: true } });
  script.scenes.forEach((scene, index) => {
    p.newPage();
    const first = p.pages.length - 1;
    if (scene.actHeading !== null) p.place([centred(scene.actHeading)], 0);
    p.place([centred(`SCENE ${scene.letter}`)], 1);
    if (scene.slugline !== null) {
      const slug = capitals(scene.slugline).map((r) => ({ ...r, underline: true }));
      p.place(wrap(slug, WIDTH).map((r) => piece(0, r)), 1);
    }
    if (scene.cast.length) p.place(wrap([run(`(${scene.cast.join(', ')})`)], WIDTH).map((r) => piece(0, r)), scene.slugline === null ? 1 : 0);
    for (const unit of units(scene.paragraphs)) {
      switch (unit.type) {
        case 'pageBreak': p.newPage(); break;
        case 'block':
          if (unit.lines.length > p.room(1) && unit.lines.length <= p.capacity) p.newPage();
          p.place(unit.lines, 1);
          break;
        case 'action': p.placeAction(unit.segments, c, 1); break;
        case 'speech': p.placeSpeech(doubled(unit.speech), 1, more, contd); break;
        case 'dual': {
          const l = doubled(unit.left), r = doubled(unit.right);
          if (Math.max(speechHeight(l), speechHeight(r)) > p.capacity) {
            for (const s of [l, r]) {
              const cue = wrap(s.cue.flatMap((x) => x.runs), c.cueWidth).map((x) => piece(c.cue, x));
              p.placeSpeech({ name: s.name, cue, segments: s.segments, columns: c }, 1, more, contd);
            }
          } else {
            p.placeSideBySide(l, r, 1);
          }
          break;
        }
      }
    }
    if (scene.end !== null) p.place([centred(scene.end)], 2);
    for (let k = 0; k < p.pages.length - first; k++) owners.push(index);
  });
  const pages = p.finished();
  return { pages, pageScenes: owners.slice(0, pages.length), linesPerPage };
}

/** `MulticamLayout.pageCount(fountain:)`: the pages an episode's scenes would print on (Write's footer). */
export function multicamPageCount(texts: readonly string[]): number {
  const written = texts.filter((t) => !blank(t));
  if (!written.length) return 0;
  const scenes = written.map((t, i): MulticamScene => ({ letter: sceneLetter(i), actHeading: null, slugline: null, cast: [],
    paragraphs: fountainParagraphs(t).map(cloneParagraph), end: null, fragmentID: null }));
  return multicamLayout({ title: '', titlePage: [], scenes }).pages.length;
}

// MARK: PDF (Typst)

/** `MulticamTypst.source`: the screenplay's title page, then every line placed, each page numbered ("2.") with the
 *  scene's letter under it when the preset asks. */
export function multicamTypst(m: CompiledManuscript, o: { fauxBold?: boolean } = {}): string {
  const script = multicamScript(m);
  const size = pageInches(m.preset.render.page.size);
  const layout = multicamLayout(script, size.height);
  const left = LEFT_MARGIN * 72, top = TOP_MARGIN * 72;
  const faux = o.fauxBold ?? false;
  const place = (x: number, y: number, runs: Run[]) => `#place(top + left, dx: ${num(x)}pt, dy: ${num(y)}pt)[${markup(runs, false, faux)}]\n`;
  let out = screenplayPreamble(m, screenplayFontFamilies(m));
  if (m.preset.render.titlePage.include) out += screenplayTitlePage(m, script.titlePage, layout.linesPerPage, faux);
  const letters = m.preset.render.screenplay?.sceneLetterInHeader === true;
  layout.pages.forEach((page, n) => {
    const number = `${n + 1}.`;
    let body = place(left + (WIDTH - count(number)) * COLUMN, top / 2, [run(number)]);
    if (letters && n < layout.pageScenes.length) {
      const letter = `(${script.scenes[layout.pageScenes[n]].letter})`;
      body += place(left + (WIDTH - count(letter)) * COLUMN, top / 2 + LINE, [run(letter)]);
    }
    for (const l of page.lines) body += place(left + l.column * COLUMN, top + l.row * LINE, l.runs);
    out += `#page[\n${body}]\n`;
  });
  return out;
}

export const multicamPageCountOf = (m: CompiledManuscript): number =>
  multicamLayout(multicamScript(m), pageInches(m.preset.render.page.size).height).pages.length;

// MARK: Word

/** `MulticamDocx.write`'s parts, in the order the Swift adds them. */
export function multicamDocxEntries(m: CompiledManuscript, o: { date?: Date; properties?: Record<string, string> } = {}): [string, string][] {
  const D = docxBits;
  const script = multicamScript(m);
  const r = m.preset.render;
  const { width, height } = pageInches(r.page.size);
  const letters = r.screenplay?.sceneLetterInHeader === true;
  const left = Math.max(r.page.margins, LEFT_MARGIN);
  const properties = o.properties ?? {};
  const hasProperties = Object.keys(properties).length > 0;
  const sectPr = (header: number | null, restart = false) => {
    const margins = `<w:pgMar w:top="${twips(r.page.margins)}" w:right="${twips(r.page.margins)}" w:bottom="${twips(r.page.margins)}" w:left="${twips(left)}" w:header="${twips(0.5)}" w:footer="${twips(0.5)}" w:gutter="0"/>`;
    return '<w:sectPr>' + (header !== null ? `<w:headerReference w:type="default" r:id="rIdHeader${header}"/>` : '')
      + `<w:type w:val="nextPage"/><w:pgSz w:w="${twips(width)}" w:h="${twips(height)}"/>${margins}`
      + (restart ? '<w:pgNumType w:start="1"/>' : '') + '</w:sectPr>';
  };
  let body = '';
  if (r.titlePage.include) {
    const field = (key: string) => script.titlePage.find((f) => f.key === key)?.value;
    body += D.paragraph('PlayTitle', D.text((field('title') ?? m.title).toUpperCase()));
    for (const key of ['credit', 'author']) { const v = field(key); if (v !== undefined) body += D.paragraph('Byline', D.text(v)); }
    (field('contact') ?? '').split('\n').filter((l) => l !== '').forEach((line, i) => { body += D.paragraph(i === 0 ? 'ContactFirst' : 'Contact', D.text(line)); });
    if (script.scenes.length) body += `<w:p><w:pPr>${sectPr(null)}</w:pPr></w:p>`;
  }
  let headers: string[] = [];
  script.scenes.forEach((scene, index) => {
    const breakBefore = index > 0;
    let first = true;
    const heading = (style: string, content: string) => {
      body += D.paragraph(style, content, true, first && breakBefore && !letters);
      first = false;
    };
    if (scene.actHeading !== null) heading('ActHeading', D.text(scene.actHeading));
    heading('SceneLetter', D.text(`SCENE ${scene.letter}`));
    if (scene.slugline !== null) body += D.paragraph('SceneHeading', D.runs(capitals(scene.slugline)));
    if (scene.cast.length) body += D.paragraph('SceneCast', D.text(`(${scene.cast.join(', ')})`), true);
    body += paragraphsXML(scene.paragraphs);
    if (scene.end !== null) body += D.paragraph('ActEnd', D.text(scene.end), true);
    if (letters) {
      headers.push(scene.letter);
      if (index < script.scenes.length - 1) body += `<w:p><w:pPr>${sectPr(headers.length, index === 0)}</w:pPr></w:p>`;
    }
  });
  if (!letters) headers = [''];
  const document = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<w:document xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships"><w:body>${body}${sectPr(headers.length, !letters || script.scenes.length === 1)}</w:body></w:document>`;
  const parts: [string, string][] = [
    ['[Content_Types].xml', D.contentTypes(headers.length, hasProperties)],
    ['_rels/.rels', D.rootRels(hasProperties)],
    ['word/document.xml', document],
    ['word/styles.xml', styles(r, m.language)],
    ['word/settings.xml', DOCX_SETTINGS],
    ['word/_rels/document.xml.rels', D.documentRels(headers.length)],
  ];
  headers.forEach((letter, i) => parts.push([`word/header${i + 1}.xml`, header(letter)]));
  parts.push(['docProps/core.xml', docxCore(m, o.date ?? new Date())], ['docProps/app.xml', docxApp(m)]);
  if (hasProperties) parts.push(['docProps/custom.xml', docxCustom(properties)]);
  return parts;
}

export function writeMulticamDocx(m: CompiledManuscript, o: { date?: Date; properties?: Record<string, string> } = {}): Uint8Array {
  const zip = new ZipWriter();
  for (const [name, text] of multicamDocxEntries(m, o)) zip.add(name, text);
  return zip.finish();
}

function paragraphsXML(ps: readonly Paragraph[]): string {
  const D = docxBits;
  let out = '';
  ps.forEach((p, i) => {
    const lines = p.lines.map((l) => D.runs(l)).join(D.LINE_BREAK);
    const next = i + 1 < ps.length ? ps[i + 1].element : 'blank';
    const speechEnds = !['dialogue', 'parenthetical', 'lyric'].includes(next);
    switch (p.element) {
      case 'character': out += D.paragraph('Character', D.runs(p.lines[0] ?? [], false, '^')); break;
      case 'dialogue': out += D.paragraph('Dialogue', lines, speechEnds); break;
      case 'parenthetical': out += D.paragraph('Parenthetical', lines, speechEnds); break;
      case 'lyric': out += D.paragraph('Lyric', lines, speechEnds); break;
      case 'action': out += D.paragraph('Action', lines, true); break;
      case 'transition': out += D.paragraph('Transition', lines, true); break;
      case 'sceneHeading': out += D.paragraph('SceneHeading', D.runs(capitals(p.lines[0] ?? [])), true); break;
      case 'centered': out += D.paragraph('Centred', lines, true); break;
      case 'pageBreak': out += D.PAGE_BREAK; break;
      default: out += D.paragraph('Action', lines, true);
    }
  });
  return out;
}

function styles(r: RenderSettings, language: string): string {
  const size = whole(r.font.size * 2, 2, 3276);
  const font = xmlEscape(r.font.family);
  const style = (id: string, name: string, pPr = '', rPr = '') =>
    `<w:style w:type="paragraph" w:styleId="${id}"><w:name w:val="${name}"/><w:basedOn w:val="Normal"/><w:qFormat/>`
    + (pPr === '' ? '' : `<w:pPr>${pPr}</w:pPr>`) + (rPr === '' ? '' : `<w:rPr>${rPr}</w:rPr>`) + '</w:style>';
  const ind = (left: number, width: number) => `<w:ind w:left="${twips(left)}" w:right="${twips(Math.max(0, 6.0 - left - width))}"/>`;
  const centred = '<w:jc w:val="center"/>', keep = '<w:keepNext/>', caps = '<w:caps/>', underline = '<w:u w:val="single"/>';
  const elements = [
    style('ActHeading', 'Act Heading', centred + keep, caps + underline),
    style('SceneLetter', 'Scene Letter', centred + keep, caps + underline),
    style('SceneHeading', 'Scene Heading', keep, caps + underline),
    style('SceneCast', 'Scene Cast', keep, caps),
    style('Action', 'Action', '', caps),
    style('Character', 'Character', ind(2.2, 3.8) + keep, caps),
    style('Parenthetical', 'Parenthetical', ind(1.6, 2.5) + keep),
    style('Dialogue', 'Dialogue', ind(1.0, 3.5) + '<w:spacing w:line="480" w:lineRule="auto"/>'),
    style('Lyric', 'Lyric', ind(1.0, 3.5), '<w:i/>'),
    style('Transition', 'Transition', '<w:jc w:val="right"/>', caps),
    style('ActEnd', 'Act End', centred, caps + underline),
    style('Centred', 'Centred', centred),
    style('PlayTitle', 'Title', `<w:spacing w:before="${twips(3)}" w:after="240"/>` + centred),
    style('Byline', 'Byline', '<w:spacing w:after="240"/>' + centred),
    style('ContactFirst', 'Contact', `<w:spacing w:before="${twips(2)}"/>`),
    style('Contact', 'Contact Lines'),
    '<w:style w:type="paragraph" w:styleId="Header"><w:name w:val="header"/><w:basedOn w:val="Normal"/><w:pPr><w:jc w:val="right"/></w:pPr></w:style>',
  ];
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<w:styles xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main">`
    + `<w:docDefaults><w:rPrDefault><w:rPr><w:rFonts w:ascii="${font}" w:hAnsi="${font}" w:eastAsia="${font}" w:cs="${font}"/><w:sz w:val="${size}"/><w:szCs w:val="${size}"/><w:lang w:val="${xmlEscape(language === '' ? 'en-US' : language)}"/></w:rPr></w:rPrDefault>`
    + '<w:pPrDefault><w:pPr><w:spacing w:after="0" w:line="240" w:lineRule="auto"/></w:pPr></w:pPrDefault></w:docDefaults>'
    + '<w:style w:type="paragraph" w:default="1" w:styleId="Normal"><w:name w:val="Normal"/><w:qFormat/></w:style>'
    + elements.join('')
    + '</w:styles>';
}

/** The page number at the right ("2."), and the scene's letter under it when there's one ("(B)"). */
function header(letter: string): string {
  const field = '<w:r><w:fldChar w:fldCharType="begin"/></w:r><w:r><w:instrText xml:space="preserve"> PAGE </w:instrText></w:r><w:r><w:fldChar w:fldCharType="separate"/></w:r><w:r><w:t>1</w:t></w:r><w:r><w:fldChar w:fldCharType="end"/></w:r>';
  const letterLine = letter === '' ? '' : `<w:p><w:pPr><w:pStyle w:val="Header"/></w:pPr>${docxBits.text(`(${letter})`)}</w:p>`;
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<w:hdr xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">`
    + `<w:p><w:pPr><w:pStyle w:val="Header"/></w:pPr>${field}${docxBits.text('.')}</w:p>${letterLine}</w:hdr>`;
}
